import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Link } from 'react-router-dom';
import CreateSiteModal from './components/CreateSiteModal';
import UserAvatars from '../../../components/UserAvatars';
import ConfirmModal from '../../../components/ConfirmModal';
import EditSiteModal from './components/EditSiteModal';
import SitemapUrlModal from './components/SitemapUrlModal';

const Sites = () => {
  const { fetchWithAuth, user, currentLang, API_URL, getCsrfToken } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingSites, setCheckingSites] = useState(new Set());
  const [deletingSites, setDeletingSites] = useState(new Set());
  const [createModal, setCreateModal] = useState({ isOpen: false });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [editModal, setEditModal] = useState({ isOpen: false, site: null });
  const [sitemapModal, setSitemapModal] = useState({ isOpen: false, site: null });

  useEffect(() => {
    fetchSites();
  }, [currentLang]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        setError(data.message || 'Ошибка загрузки сайтов');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setError('Ошибка сети при загрузке сайтов');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const getStatusBadge = (status) => {
    return status === 1 ? (
      <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-800 rounded-full">
        Активен
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">
        Неактивен
      </span>
    );
  };

  const getHttpsBadge = (https) => {
    return https === 1 ? (
      <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-800 rounded-full">
        HTTPS
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">
        HTTP
      </span>
    );
  };

  const getTarifBadge = (tarif) => {
    return tarif === 0 ? (
      <span className="px-2 py-1 text-xs font-bold bg-blue-100 text-gray-800 rounded-full">
        Без продвижения
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-bold bg-gray-100 text-blue-800 rounded-full">
        Старт
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (!score || score === 0) return '#9CA3AF'; // gray-400
    if (score >= 80) return '#10B981'; // green-500
    if (score >= 60) return '#F59E0B'; // yellow-500
    if (score >= 40) return '#F97316'; // orange-500
    return '#EF4444'; // red-500
  };

  const handleCheckSite = async (site) => {
    try {
      setCheckingSites(prev => new Set(prev).add(site.id));
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${site.id}/check`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        // Обновляем список сайтов
        await fetchSites();
      }
    } catch (error) {
      // Ошибка обновления сайта
    } finally {
      setCheckingSites(prev => {
        const newSet = new Set(prev);
        newSet.delete(site.id);
        return newSet;
      });
    }
  };

  const showDeleteConfirm = (site) => {
    setSiteToDelete(site);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!siteToDelete) return;

    try {
      setDeletingSites(prev => new Set(prev).add(siteToDelete.id));
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites/${siteToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': currentCsrfToken,
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        // Обновляем список сайтов
        await fetchSites();
        showNotification(currentLang === 'ru' ? 'Сайт удален!' : 'Site deleted!', 'success');
      } else {
        showNotification(data.message || (currentLang === 'ru' ? 'Ошибка удаления сайта' : 'Error deleting site'), 'error');
      }
    } catch (error) {
      showNotification(currentLang === 'ru' ? 'Ошибка сети при удалении сайта' : 'Network error while deleting site', 'error');
    } finally {
      setDeletingSites(prev => {
        const newSet = new Set(prev);
        newSet.delete(siteToDelete.id);
        return newSet;
      });
      setSiteToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSiteToDelete(null);
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Автоматическое скрытие через 3 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const handleSiteCreated = () => {
    fetchSites();
  };
  console.log(user);
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
        <h2 className="text-xl sm:text-2xl font-bold dark:text-white">{currentLang === 'ru' ? 'Сайты' : 'Sites'}</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          {user?.role === 'admin' && (
          <button
            onClick={() => setCreateModal({ isOpen: true })}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>{currentLang === 'ru' ? 'Добавить сайт' : 'Add Site'}</span>
          </button>
          )}
          <button
            onClick={fetchSites}
            disabled={loading}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {sites.map((site) => (
            <div key={site.id} className={`bg-white rounded-lg shadow-md p-4 sm:p-6 border dark:bg-navy-700 dark:text-white dark:border-white/10 ${site.status == 0 ? 'opacity-30 hover:opacity-100' : ''}`}>
              <div className="flex flex-col lg:flex-row justify-between items-start mb-4 gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">
                    <Link
                      to={`/seo/sites/${site.id}`}
                      className="text-blue-600 inline-flex items-center break-all"
                    >
                      <img src={`https://www.google.com/s2/favicons?domain=${site.dns}`} alt="favicon" className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{site.dns}</span>
                    </Link>
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    { site.udp && (
                      <span className="text-xs sm:text-sm text-gray-600">
                        {formatDate(site.udp * 1000)}
                      </span>
                    )}
                    <button
                      onClick={() => handleCheckSite(site)}
                      className="p-1 sm:p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      title={currentLang === 'ru' ? 'Обновить информацию сайта' : 'Update site info'}
                      disabled={checkingSites.has(site.id)}
                    >
                      {checkingSites.has(site.id) ? (
                        <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-500"></div>
                      ) : (
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {getStatusBadge(site.status)}
                    {getHttpsBadge(site.https)}
                    {getTarifBadge(site.tarif)}
                  </div>
                  {/* Дополнительная информация о сайте */}
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-gray-600">
                    {site.site_cms && (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                        {site.site_cms}
                      </span>
                    )}
                    {site.site_ip && (
                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                        {site.site_ip}
                      </span>
                    )}
                    {site.site_server && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded truncate max-w-32" title={site.site_server}>
                        {site.site_server}
                      </span>
                    )}
                  </div>
                  {/* Аватары пользователей с доступом */}
                  {site.access_users && site.access_users.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-600">
                        {currentLang === 'ru' ? 'Пользователи:' : 'Users:'}
                      </span>
                      <UserAvatars 
                        users={site.access_users} 
                        maxVisible={3} 
                        size="xs" 
                      />
                    </div>
                  )}
                </div>
                
                
                
                {/* Кнопки действий */}
                <div className="flex flex-col mb-4 gap-4">
                  <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                    <Link
                      to={`/seo/sites/${site.id}`}
                      className="px-3 py-1 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm lg:ml-auto"
                    >
                      {currentLang === 'ru' ? 'Подробнее' : 'Details'}
                    </Link>
                    {/* Значки Яндекс и Topvisor */}
                    {site.ymetrica && site.ymetrica !== '0' && String(site.ymetrica).trim() !== '' && site.ymetrica_token && (
                        <span title={currentLang === 'ru' ? 'Яндекс.Метрика подключена' : 'Yandex Metrica connected'} className="inline-flex items-center">
                          <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#ffdb4d"/><text x="12" y="16" textAnchor="middle" fontSize="12" fill="#222" fontWeight="bold">Я</text></svg>
                        </span>
                      )}
                      {site.topvisor_project_id && site.topvisor_api === true && site.topvisor_user_id && (
                        <span title={currentLang === 'ru' ? 'Topvisor подключён' : 'Topvisor connected'} className=" inline-flex items-center">
                          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 17l6-6 4 4 8-8" /><rect x="2" y="17" width="20" height="5" rx="2" fill="#3b82f6"/></svg>
                        </span>
                      )}
                    <Link
                      to={`/seo/positions/${site.id}`}
                      className="px-3 py-1 bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-lg hover:from-green-500 hover:to-blue-600 transition-colors flex items-center space-x-2 shadow-md text-sm"
                      title={currentLang === 'ru' ? 'Позиции сайта в поиске' : 'Site positions'}
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17l6-6 4 4 8-8" />
                      </svg>
                      <span>{currentLang === 'ru' ? 'Позиции' : 'Positions'}</span>
                    </Link>
                    {user?.id === site.ucr && (
                      <>
                        <button
                          onClick={() => setEditModal({ isOpen: true, site })}
                          className="p-1 sm:p-2 text-brand-500 hover:text-brand-700 hover:bg-brand-50 rounded transition-colors"
                          title={currentLang === 'ru' ? 'Редактировать сайт' : 'Edit site'}
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </>
                    )}
                    {site.status === 0 && user?.id === site.ucr && (
                      <>
                        <button
                          onClick={() => showDeleteConfirm(site)}
                          className="p-1 sm:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title={currentLang === 'ru' ? 'Удалить сайт' : 'Delete site'}
                          disabled={deletingSites.has(site.id)}
                        >
                          {deletingSites.has(site.id) ? (
                            <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-red-500"></div>
                          ) : (
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                  {/* Счетчики - скрываем на мобильных */}
                  <div className="grid grid-cols-3 sm:flex sm:justify-end space-x-4 sm:space-x-6 sm:ml-auto">
                    <div className="flex flex-col items-center space-y-1">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            stroke={getScoreColor(site.seo_score)}
                            strokeWidth="3"
                            strokeDasharray={`${(site.seo_score || 0) * 1}, 100`}
                            strokeLinecap="round"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-gray-700">{site.seo_score || 0}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">{currentLang === 'ru' ? 'SEO' : 'SEO'}</div>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-1">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            stroke={getScoreColor(site.security_score)}
                            strokeWidth="3"
                            strokeDasharray={`${(site.security_score || 0) * 1}, 100`}
                            strokeLinecap="round"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-gray-700">{site.security_score || 0}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">{currentLang === 'ru' ? 'Безопасность' : 'Security'}</div>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-1">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            stroke={getScoreColor(site.pagespeed_score)}
                            strokeWidth="3"
                            strokeDasharray={`${(site.pagespeed_score || 0) * 1}, 100`}
                            strokeLinecap="round"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-gray-700">{site.pagespeed_score || 0}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">{currentLang === 'ru' ? 'Скорость' : 'Speed'}</div>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-1">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            stroke={getScoreColor(site.schema_score)}
                            strokeWidth="3"
                            strokeDasharray={`${(site.schema_score || 0) * 1}, 100`}
                            strokeLinecap="round"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-gray-700">{site.schema_score || 0}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">{currentLang === 'ru' ? 'Схема' : 'Schema'}</div>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-1">
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            className="text-gray-200"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            stroke={getScoreColor(site.meta_score)}
                            strokeWidth="3"
                            strokeDasharray={`${(site.meta_score || 0) * 1}, 100`}
                            strokeLinecap="round"
                            fill="none"
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-gray-700">{site.meta_score || 0}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600">{currentLang === 'ru' ? 'Мета' : 'Meta'}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Статистика сайта */}
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{currentLang === 'ru' ? 'Sitemap' : 'Sitemap'}</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                        {site.sitemaps_link ? (
                          <button
                            onClick={() => setSitemapModal({ isOpen: true, site })}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title={currentLang === 'ru' ? 'Просмотреть sitemap' : 'View sitemap'}
                          >
                            {site.sitemaps_link}
                          </button>
                        ) : (
                          <span className="text-gray-400">{currentLang === 'ru' ? 'Не указан' : 'Not specified'}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{currentLang === 'ru' ? 'Домен до' : 'Domain until'}</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900">{formatDate(site.whois_exp)}</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{currentLang === 'ru' ? 'SSL до' : 'SSL until'}</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900">
                        {site.ssl_date ? formatDate(site.ssl_date * 1000) : (currentLang === 'ru' ? 'Не указан' : 'Not specified')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{currentLang === 'ru' ? 'Страниц' : 'Pages'}</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900">{site.pages_count || 0}</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{currentLang === 'ru' ? 'Задач' : 'Tasks'}</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900">{site.tasks_count || 0}</div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{currentLang === 'ru' ? 'Ссылки' : 'Links'}</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900">{site.active_links_count || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && sites.length === 0 && !error && (
        <div className="text-center py-8 text-gray-500">
          {currentLang === 'ru' ? 'Сайты не найдены' : 'Sites not found'}
        </div>
      )}

      {/* Модальное окно создания сайта */}
      <CreateSiteModal
        isOpen={createModal.isOpen}
        onClose={() => setCreateModal({ isOpen: false })}
        onSiteCreated={handleSiteCreated}
      />

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? `Вы уверены, что хотите удалить сайт "${siteToDelete?.dns}"? Это действие нельзя отменить.` 
          : `Are you sure you want to delete site "${siteToDelete?.dns}"? This action cannot be undone.`
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />

      {/* Модалка редактирования сайта */}
      <EditSiteModal
        isOpen={editModal.isOpen}
        site={editModal.site}
        onClose={() => setEditModal({ isOpen: false, site: null })}
        onUpdate={fetchSites}
      />

      {/* Модалка просмотра sitemap */}
      <SitemapUrlModal
        site={sitemapModal.site}
        isOpen={sitemapModal.isOpen}
        onClose={() => setSitemapModal({ isOpen: false, site: null })}
        viewOnly={true}
      />
    </div>
  );
};

export default Sites;
